@if (isVisible) {
  <div class="picture-overlay-backdrop"
    (click)="onBackdropClick()">
    <div class="picture-overlay-content" [class.markup-active]="showMarkupEditor" (click)="$event.stopPropagation()">
      <!-- Close Button -->
      <button
        (click)="close()"
        aria-label="Close"
        class="close"
        mat-icon-button
        >
        <mat-icon>close</mat-icon>
      </button>
      <!-- Loading Indicator (shown while Swiper initializes) -->
      @if (!isContentReady) {
        <div class="loading-container">
          <mat-spinner diameter="60" color="primary"></mat-spinner>
          <p class="loading-text">Loading images...</p>
        </div>
      }
      <!-- Swiper Carousel (always rendered, but hidden behind loading indicator until ready) -->
      <div class="swiper-wrapper" [class.hidden]="!isContentReady">
        <swiper-container
          #swiperContainer
          init="false"
          keyboard="true"
          navigation="true"
          pagination="true"
          slides-per-view="1"
          >
          @for (picture of picturesList; track picture; let i = $index; let isFirst = $first) {
            <swiper-slide>
              <div class="slide-header">
                <!-- Download all button - shown only when there are multiple pictures -->
                @if (picturesList.length > 1) {
                  <button
                    (click)="downloadAll()"
                    [disabled]="downloadingAll"
                    mat-icon-button
                    class="download-all-button"
                    [matTooltipDisabled]="downloadingAll"
                    title="Download all"
                    >
                    @if (!downloadingAll) {
                      <mat-icon>archive</mat-icon>
                    }
                    @if (downloadingAll) {
                      <mat-spinner diameter="20" color="primary" mode="indeterminate"></mat-spinner>
                    }
                  </button>
                }

                <!-- Markup / Annotate button -->
                @if (mapService.isPictureOverlayEditable) {
                  <button
                    (click)="openMarkupEditor(picture)"
                    mat-icon-button
                    class="markup-button"
                    title="Annotate"
                    >
                    <mat-icon>edit</mat-icon>
                  </button>
                }

                <!-- Photo proof (GeoStamp): details panel toggle, only for photos that carry a proof -->
                @if (hasProof(picture)) {
                  <button
                    (click)="toggleProofPanel()"
                    mat-icon-button
                    class="proof-button"
                    [class.active]="proofPanelOpen"
                    title="Photo details"
                    data-testid="proof-button"
                    >
                    <mat-icon>info_outline</mat-icon>
                  </button>
                }

                <!-- Individual download button -->
                <button
                  (click)="downloadPicture(picture)"
                  aria-label="Download {{ picture.source_name }}"
                  mat-icon-button
                  class="download-button"
                  title="Download {{ picture.source_name || 'image' }}"
                  >
                  <mat-icon>download</mat-icon>
                </button>
              </div>
              <div class="slide-content" [style.--blur-src]="picture.thumb_url || picture.original_url ? 'url(' + (picture.thumb_url || picture.original_url) + ')' : 'none'">
                <!-- Load all images immediately for simplicity -->
                @if (picture.original_url || picture.thumb_url) {
                  <img
                    [src]="getDisplayImageSrc(picture)"
                    [attr.alt]="picture.source_name || 'Picture ' + (i + 1)"
                    class="slide-image"
                    (load)="onImageLoad($event)"
                    (error)="onImageError($event, picture)"
                    />
                }
                <!-- Photo proof details (GeoStamp): the rows a reviewer reads, as on the phone -->
                @if (proofPanelOpen && hasProof(picture)) {
                  <app-photo-proof-panel
                    [picture]="picture"
                    [thresholdM]="verifiedThresholdM"
                    [showingOriginal]="showingOriginal(picture)"
                    (toggleOriginal)="toggleOriginal(picture)"
                    ></app-photo-proof-panel>
                }

                <!-- Show error if no URL available -->
                @if (!picture.original_url && !picture.thumb_url) {
                  <div class="image-error">
                    <mat-icon>broken_image</mat-icon>
                    <p>Image URL not available</p>
                  </div>
                }

                <!-- PDF page navigation (only shown when PDF has markups) -->
                @if (pdfPageState[picture.id]?.totalPages > 1 && pdfHasMarkups[picture.id]) {
                  <div class="pdf-page-nav">
                    <button mat-icon-button
                            [disabled]="pdfPageState[picture.id].currentPage <= 1 || pdfPageState[picture.id].loading"
                            (click)="goToPdfPage(picture, pdfPageState[picture.id].currentPage - 1)">
                      <mat-icon>chevron_left</mat-icon>
                    </button>
                    <span class="pdf-page-indicator">
                      @if (pdfPageState[picture.id].loading) {
                        <mat-spinner diameter="16"></mat-spinner>
                      }
                      @if (!pdfPageState[picture.id].loading) {
                        <span>
                          Page {{ pdfPageState[picture.id].currentPage }} / {{ pdfPageState[picture.id].totalPages }}
                        </span>
                      }
                    </span>
                    <button mat-icon-button
                            [disabled]="pdfPageState[picture.id].currentPage >= pdfPageState[picture.id].totalPages || pdfPageState[picture.id].loading"
                            (click)="goToPdfPage(picture, pdfPageState[picture.id].currentPage + 1)">
                      <mat-icon>chevron_right</mat-icon>
                    </button>
                  </div>
                }
              </div>
            </swiper-slide>
          }
        </swiper-container>
      </div> <!-- End swiper-wrapper -->
    </div>

    <!-- Markup Editor Overlay -->
    <!-- Stop clicks bubbling to the backdrop's (click)="onBackdropClick()", which
         would otherwise close the whole overlay whenever a toolbar control is used. -->
    @if (showMarkupEditor) {
      <app-markup-editor
        (click)="$event.stopPropagation()"
        [config]="markupEditorConfig"
        (closed)="closeMarkupEditor()"
        (saved)="onMarkupSaved($event)">
      </app-markup-editor>
    }
  </div>
}
